import React, { useEffect } from 'react'
import { animated, easings, useSpringRef, useTransition } from '@react-spring/web'
import { observer } from 'mobx-react-lite'

import { isInfoCard } from '@trivie/core/src/models/game/GameQuiz'
import { Question } from './Question'
import { ProgressDots } from '../progress-dots'
import { InfoCard } from '../info-card'
import { Button } from '../button/button'
import { color } from '../../theme'

export const QuizItemList = observer((props: any) => {
  const { quiz, branding, mode = 'quiz' } = props
  const transitionRef = useSpringRef()

  const { items, activeIdx } = quiz

  const transitions = useTransition(activeIdx, {
    ref: transitionRef,
    easing: easings.easeInOutCubic,
    // Key by stable question id (not the reused integer index) so entering/leaving
    // nodes reconcile against a stable identity during fast transitions.
    keys: (idx) => items?.[idx]?.id ?? idx,
    exitBeforeEnter: true,
    from: { transform: 'translate3d(100%,0,0)', opacity: 0 },
    enter: { transform: 'translate3d(0%,0,0)', opacity: 1 },
    leave: { transform: 'translate3d(-70%,0,0)', opacity: 0 },
  })

  useEffect(() => {
    transitionRef.start()
    return () => {
      transitionRef.stop()
    }
  }, [activeIdx])

  const renderItem = (item) => {
    if (isInfoCard(item)) {
      return (
        <InfoCard
          branding={branding}
          footer={
            <Button
              style={{ backgroundColor: branding.accent_color }}
              textStyle={{ color: color.white }}
              fullWidth
              tx="Common.next"
              onPress={quiz.next}
            />
          }
          containerStyle={{ padding: 20 }}
          card={item}
        />
      )
    }
    // Game questions and survey questions share the same render pipeline.
    return <Question branding={branding} question={item} mode={mode} />
  }

  const display = items?.map((item) => ({ style }) => (
    <animated.div style={{ ...ITEM_CONTAINER, ...style }}>{renderItem(item)}</animated.div>
  ))

  return (
    <>
      <ProgressDots branding={branding} items={items} active={activeIdx} mode={mode} />
      {display &&
        transitions((style, i) => {
          const Item = display[Number(i)]
          return Item ? <Item style={style} /> : null
        })}
    </>
  )
})

const ITEM_CONTAINER = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  opacity: 1,
  overflowY: 'auto',
  padding: 20,
  paddingLeft: 0,
  paddingRight: 0,
  height: '100%',
  gap: 24,
  justifyContent: 'space-between',
}
